import { useContext, useEffect, useState } from "react";
import { Geometry } from "geojson";

import { MapContext } from "../MapProvider";

interface LayerChangeEvent {
  layerId: string;
  geometryType?: Geometry["type"];
  editingLayer?: unknown;
}

/**
 * Custom hook to check if the current editing layer matches a specific geometry type.
 * Returns true if the current editing layer's GeometryType matches the provided type.
 *
 * @param geometryType - The geometry type to check for (e.g., "Point", "LineString", "Polygon")
 * @returns Boolean indicating if the current layer matches the geometry type
 */
export function useGeometryTypeCheck(geometryType: Geometry["type"]): boolean {
  const { map, drawControl } = useContext(MapContext);
  const [isMatchingType, setIsMatchingType] = useState(false);

  useEffect(() => {
    // Initial check
    const editableLayers = drawControl.current?.options?.editableLayers;

    // If no editableLayers defined, enable all geometry types
    if (!editableLayers || editableLayers.length === 0) {
      setIsMatchingType(true);
    } else {
      const currentEditingLayerId = drawControl.current?.options?.editingLayerId;
      const currentEditingLayer = editableLayers.find((layer) => layer.ID === currentEditingLayerId);

      // If no current layer or no geometry type specified, enable the button
      setIsMatchingType(!currentEditingLayer || !currentEditingLayer.GeometryType || currentEditingLayer.GeometryType === geometryType);
    }

    // Listen for layer changes
    const handleLayerChange = (e: LayerChangeEvent) => {
      // If no geometry type specified in the event, enable the button
      setIsMatchingType(!e.geometryType || e.geometryType === geometryType);
    };

    map?.on("draw.layerchange", handleLayerChange);

    return () => {
      map?.off("draw.layerchange", handleLayerChange);
    };
  }, [map, drawControl, geometryType]);

  return isMatchingType;
}
